import { ReactNode, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import useFiltersBySearchParams from "../../helpers/useFiltersBySearchParams";
import { Stack } from "@altimateai/lego";

interface TabItem {
  label: string;
  component?: ReactNode;
  disabled?: boolean;
  path?: string;
  betaMode?: boolean;
}

interface GhostTabItem {
  label: string;
  value: string;
  betaMode?: boolean;
}

interface TabProps {
  tabs: TabItem[];
  onSelectedTabChange?: (label: string) => void;
  defaultTab?: number;
  fullscreen?: boolean;
  pagedTab?: boolean;
  tabStyle?: string;
}

/**
 * @deprecated use shadcn component
 */
const Tab = ({
  tabs,
  onSelectedTabChange,
  defaultTab = 0,
  fullscreen = false,
  pagedTab = true,
  tabStyle = "",
}: TabProps) => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  // TODO: this state var can be removed and use only search params
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedTab(defaultTab);
  }, [defaultTab]);

  const activeTabs = tabs.filter(t => !t.disabled);

  const handleTabClick = (index: number, path?: string) => {
    if (path) {
      navigate(path);
    }
    setSelectedTab(index);
    if (pagedTab) {
      updateSearchParamsByKey({ tab: String(index) });
    }
    if (typeof onSelectedTabChange === "function") {
      onSelectedTabChange(activeTabs[index].label);
    }
  };

  useEffect(() => {
    if (!pagedTab) return;
    const tab = searchParams.get("tab");
    const tabNum = tab !== null ? Number(tab) : NaN;
    if (!isNaN(tabNum) && tabNum >= 0 && tabNum < activeTabs.length) {
      setSelectedTab(tabNum);
    }
  }, [activeTabs.length, pagedTab, searchParams]);

  return (
    <div
      className={classNames(tabStyle, {
        [styles.fullscreen_tab]: fullscreen,
      })}
    >
      <div className={styles.tab_labels}>
        {activeTabs.map((tab, index) => (
          <div
            key={index}
            className={classNames(styles.tab_label, {
              [styles.active]: index === selectedTab,
            })}
            onClick={() => handleTabClick(index, tab?.path)}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                handleTabClick(index, tab?.path);
              }
            }}
            data-label={tab.label}
            role="button"
            tabIndex={0}
          >
            <Stack justify="between" align="center" className="gap-xs">
              {tab.label}
              {tab.betaMode && (
                <div className={styles.beta}>
                  <div className={styles.text}>Beta</div>
                </div>
              )}
            </Stack>
          </div>
        ))}
      </div>
      <div className={classNames({ [styles.content]: fullscreen })}>
        {activeTabs[selectedTab].component}
      </div>
    </div>
  );
};

interface GhostTabProps {
  tabs: GhostTabItem[];
  selectedTab: string;
  setSelectedTab: (value: string) => void;
}

/**
 * @deprecated use shadcn component
 */
const GhostTab = ({ tabs, selectedTab, setSelectedTab }: GhostTabProps) => {
  return (
    <div className={styles.ghost_tab_container}>
      <Stack>
        {tabs.map(({ label, value, betaMode }) => (
          <div
            className={classNames(styles.tab_item, {
              [styles.active]: selectedTab === value,
            })}
            key={value}
            onClick={() => setSelectedTab(value)}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                setSelectedTab(value);
              }
            }}
            role="option"
            aria-selected={selectedTab === value}
            tabIndex={0}
          >
            {label}
            {betaMode && (
              <div className={classNames(styles.beta, "ms-2")}>
                <div className={styles.text}>Beta</div>
              </div>
            )}
          </div>
        ))}
      </Stack>
    </div>
  );
};

interface TabWithOutletItem {
  label: string;
  path: string;
  betaMode?: boolean;
  disabled?: boolean;
}

interface TabWithOutletProps {
  tabs: TabWithOutletItem[];
  defaultTab?: number;
}

/**
 * @deprecated use shadcn component
 */
const TabWithOutlet = ({ tabs, defaultTab = 0 }: TabWithOutletProps) => {
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const navigate = useNavigate();
  const location = useLocation();

  const activeTabs = tabs.filter(t => !t.disabled);

  useEffect(() => {
    activeTabs.forEach((tab, index) => {
      if (location.pathname.includes(tab.path.split("?")[0])) {
        setSelectedTab(index);
      }
    });
  }, [location, tabs]);

  const handleTabClick = (index: number, path?: string) => {
    if (path) {
      navigate(path);
    }
    setSelectedTab(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number, path?: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleTabClick(index, path);
    }
  };

  return (
    <div className={styles.tab_labels}>
      {activeTabs.map((tab, index) => (
        <div
          key={index}
          className={classNames(styles.tab_label, {
            [styles.active]: index === selectedTab,
          })}
          onClick={() => handleTabClick(index, tab?.path)}
          onKeyDown={e => handleKeyDown(e, index, tab?.path)}
          role="button"
          aria-label={`${tab.label} tab`}
          tabIndex={0}
        >
          <Stack justify="between" align="center" className="gap-xs">
            {tab.label}
            {tab.betaMode && (
              <div className={styles.beta}>
                <div className={styles.text}>Beta</div>
              </div>
            )}
          </Stack>
        </div>
      ))}
    </div>
  );
};

export { Tab, GhostTab, TabWithOutlet };
